<template>
  <div class="p-8">
    <section
      class="flex flex-col xl:flex-row xl:items-end xl:justify-between pb-8 gap-4"
    >
      <div>
        <h1 class="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
          <!-- name is already translated in controller -->
          {{ countryName }}
        </h1>
        <p class="text-gray-500">
          {{ benchmarks.length }}
          <Translation
            name="benchmarks_show.benchmarks"
            default="Benchmarks "
          />
          <Translation name="benchmarks_show.across" default="across" />
          {{ regionCount }}
          <Translation name="benchmarks_show.regions" default="regions" />
        </p>
      </div>
      <div class="flex flex-col sm:flex-row sm:items-center gap-2">
        <SearchableDropdown
          v-model="selectedRegion"
          :options="regions"
          :all-label="props.allRegionsTranslation"
        >
          <template #placeholder>
            <Translation
              name="benchmarks_show.all_regions"
              default="All Regions"
            />
          </template>
        </SearchableDropdown>
        <div class="relative" ref="yearDropdown">
          <button
            @click="isYearDropdownOpen = !isYearDropdownOpen"
            class="filter-dropdown"
          >
            <span v-if="selectedYear !== props.allYearsTranslation">
              {{ selectedYear }}</span
            >
            <Translation
              v-else
              name="benchmarks_show.all_years"
              default="All Years"
            />
            <ChevronDown
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': isYearDropdownOpen }"
            />
          </button>

          <div
            v-if="isYearDropdownOpen"
            class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
          >
            <ul class="max-h-60 overflow-auto text-sm">
              <li
                v-for="year in years"
                :key="year"
                @click="
                  selectedYear = year;
                  isYearDropdownOpen = false;
                "
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
                :style="
                  year === selectedYear ? { backgroundColor: '#EAF7F6' } : {}
                "
              >
                {{ year }}
                <svg
                  v-if="year === selectedYear"
                  class="w-4 h-4 flex-shrink-0 text-brand-600"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M16.667 5L7.5 14.167L3.333 10" />
                </svg>
              </li>
            </ul>
          </div>
        </div>
        <div class="relative" ref="methodologyDropdown">
          <button
            @click="isMethodologyDropdownOpen = !isMethodologyDropdownOpen"
            class="filter-dropdown"
          >
            <span v-if="selectedMethodology.value">
              {{ selectedMethodology.label }}</span
            >
            <Translation
              v-else
              name="benchmarks_show.all_methodologies"
              default="All Methodologies"
            />

            <ChevronDown
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': isMethodologyDropdownOpen }"
            />
          </button>

          <div
            v-if="isMethodologyDropdownOpen"
            class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
          >
            <ul class="max-h-60 overflow-auto text-sm">
              <li
                v-for="method in methodologies"
                :key="method.value"
                :value="method.value"
                @click="
                  selectedMethodology = method;
                  isMethodologyDropdownOpen = false;
                "
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
                :style="
                  method.value === selectedMethodology.value
                    ? { backgroundColor: '#EAF7F6' }
                    : {}
                "
              >
                {{ method.label }}
                <svg
                  v-if="method.value === selectedMethodology.value"
                  class="w-4 h-4 flex-shrink-0 text-brand-600"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M16.667 5L7.5 14.167L3.333 10" />
                </svg>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <div
      v-if="activeFilterPills.length > 0"
      class="flex flex-wrap items-center gap-2 pb-4"
    >
      <span class="text-sm text-gray-500">Selected</span>
      <span
        v-for="pill in activeFilterPills"
        :key="pill.key"
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-25 text-brand-700"
      >
        {{ pill.label }}
        <button @click="pill.remove" class="ml-0.5 hover:text-brand-900">
          &times;
        </button>
      </span>
    </div>
    <div class="border border-solid border-gray-200 rounded-xl">
      <table class="w-full text-left">
        <thead class="hidden md:table-header-group">
          <tr class="border-b border-solid border-gray-200">
            <th class="table-header xl:w-[380px]">
              <Translation name="benchmarks_show.region" default="Region" />
            </th>
            <th class="table-header">
              <Translation
                name="benchmarks_show.micro_region"
                default="Micro-region"
              />
            </th>
            <th class="table-header">
              <Translation
                name="benchmarks_show.availability"
                default="Availability"
              />
            </th>
            <th class="table-header">
              <Translation name="benchmarks_show.year" default="Year" />
            </th>
            <th class="table-header">
              <Translation
                name="benchmarks_show.methodology"
                default="Methodology"
              />
            </th>
            <th class="table-header"></th>
          </tr>
        </thead>
        <tbody v-for="benchmark in filteredBenchmarks" :key="benchmark.id">
          <tr
            class="grid table-grid p-4 md:p-0 md:table-row gap-4 cursor-pointer hover:bg-gray-200"
            @click="toggleBenchmark(benchmark.id)"
          >
            <th
              class="col-span-1 text-gray-600 md:text-gray-900 text-sm font-semibold md:h-[72px] md:px-6 flex flex-col md:table-cell"
              :class="
                benchmarks[benchmarks.length - 1] === benchmark &&
                !isBenchmarkOpen[benchmark.id]
                  ? 'rounded-bl-xl'
                  : ''
              "
            >
              <span class="md:hidden font-bold text-gray-900">
                <Translation name="benchmarks_show.region" default="Region" />
              </span>
              {{ getDisplayRegionName(benchmark) }}
            </th>
            <th class="col-span-1 cell">
              <span class="md:hidden font-bold text-gray-900">
                <Translation
                  name="benchmarks_show.micro_region"
                  default="Micro-region"
              /></span>
              {{ benchmark.micro_region }}
            </th>
            <th
              class="md:hidden !text-center"
              :class="
                benchmarks[benchmarks.length - 1] === benchmark &&
                !isBenchmarkOpen[benchmark.id]
                  ? 'rounded-br-xl'
                  : ''
              "
            >
              <button>
                <ChevronDown
                  class="transition-transform"
                  :class="{ 'rotate-180': isBenchmarkOpen[benchmark.id] }"
                />
              </button>
            </th>
            <th class="col-span-1 cell">
              <span class="md:hidden font-bold text-gray-900">
                <Translation
                  name="benchmarks_show.availability"
                  default="Availability"
              /></span>
              <span v-if="benchmark.availability === 'For Purchase'">
                <Translation
                  name="benchmarks_show.availability_for_purchase"
                  default="For Purchase"
                />
              </span>
              <span v-else>
                <Translation
                  name="benchmarks_show.availability_public"
                  default="Public"
                />
              </span>
            </th>
            <th class="col-span-1 cell">
              <span class="md:hidden font-bold text-gray-900">
                <Translation name="benchmarks_show.year" default="Year"
              /></span>
              {{ benchmark.year }}
            </th>
            <th class="col-span-2 cell">
              <span class="md:hidden font-bold text-gray-900 mb-1">
                <Translation
                  name="benchmarks_show.methodology"
                  default="Methodology"
              /></span>
              <div class="flex items-center gap-x-2">
                <div
                  class="h-4 w-4 rounded-full flex-none"
                  :class="methodologyColor(benchmark.methodology)"
                ></div>
                {{ methodologyName(benchmark.methodology) }}
              </div>
            </th>
            <th
              class="h-[72px] hidden md:flex p-4 items-center justify-center"
              :class="
                benchmarks[benchmarks.length - 1] === benchmark &&
                !isBenchmarkOpen[benchmark.id]
                  ? 'rounded-br-xl'
                  : ''
              "
            >
              <button>
                <ChevronDown
                  class="transition-transform"
                  :class="{ 'rotate-180': isBenchmarkOpen[benchmark.id] }"
                />
              </button>
            </th>
          </tr>
          <tr v-if="isBenchmarkOpen[benchmark.id]">
            <td
              colspan="6"
              class="px-6 py-4 bg-brand-25"
              :class="
                benchmarks[benchmarks.length - 1] === benchmark
                  ? 'rounded-b-xl'
                  : ''
              "
            >
              <div class="flex flex-col gap-4">
                <div class="flex items-center justify-between">
                  <p class="text-gray-900 text-sm font-bold">
                    {{ getDisplayRegionName(benchmark) }}
                  </p>
                  <button
                    class="sm:hidden bg-white text-brand-700 border rounded-lg px-2 py-1 md:px-3 md:py-2 flex items-center text-xs md:text-sm font-semibold border-none gap-2 hover:bg-gray-200 transition-colors duration-200 ease-in-out"
                    @click="toggleInfo(benchmark.methodology)"
                  >
                    <InfoIcon />
                    <Translation
                      name="benchmarks_show.methodology"
                      default="Methodology"
                    />
                  </button>
                </div>

                <p
                  v-if="benchmark.methodology === 'anker'"
                  class="text-gray-900 text-sm max-w-[800px]"
                >
                  <Translation
                    name="benchmarks_show.anker_text"
                    default="This benchmark is calculated using the Full-Fledged Anker Methodology. The data represents living wage estimates for this specific region based on comprehensive local cost of living analysis."
                  />
                </p>
                <p
                  v-if="benchmark.methodology === 'wage-indicator'"
                  class="text-gray-900 text-sm max-w-[800px]"
                >
                  <Translation
                    name="benchmarks_show.wage_indicator_text"
                    default="This benchmark is calculated using the WageIndicator Methodology. The data represents living wage estimates for this specific region based on comprehensive local cost of living analysis."
                  />
                </p>
                <p
                  v-if="benchmark.methodology === 'fair-wage'"
                  class="text-gray-900 text-sm max-w-[800px]"
                >
                  <Translation
                    name="benchmarks_show.fair_wage_text"
                    default="This benchmark is calculated using the Fair Wage Methodology. The data represents living wage estimates for this specific region based on comprehensive local cost of living analysis."
                  />
                </p>
                <p
                  v-if="benchmark.methodology === 'living-wage-us'"
                  class="text-gray-900 text-sm max-w-[800px]"
                >
                  <Translation
                    name="benchmarks_show.living_wage_us_text"
                    default="This benchmark is calculated using the Living Wage US Methodology. The data represents living wage estimates for this specific region based on comprehensive local cost of living analysis."
                  />
                </p>
                <p
                  v-if="benchmark.methodology === 'other'"
                  class="text-gray-900 text-sm max-w-[800px]"
                >
                  <Translation
                    name="benchmarks_show.other_text"
                    default="The data represents living wage estimates for this specific region based on comprehensive local cost of living analysis."
                  />
                </p>
                <div class="flex justify-between">
                  <div class="flex gap-2">
                    <a
                      v-if="benchmark.availability_link || benchmark.link"
                      :href="benchmark.availability_link || benchmark.link"
                      class="bg-brand-600 rounded-lg px-2 py-1.5 md:px-3 md:py-2 text-white flex items-center text-xs md:text-sm font-semibold gap-2"
                    >
                      <span class="sm:hidden">
                        <Translation
                          name="benchmarks_show.view_button_small"
                          default="View"
                        />
                      </span>
                      <span class="hidden sm:block">
                        <Translation
                          name="benchmarks_show.view_button_big"
                          default="View Benchmark"
                        />
                      </span>
                      <ExternalLinkIcon class="view-external" />
                    </a>
                    <button
                      class="hidden bg-white text-brand-700 border rounded-lg px-2 py-1 md:px-3 md:py-2 sm:flex items-center text-xs md:text-sm font-semibold border-none gap-2 hover:bg-gray-200 transition-colors duration-200 ease-in-out"
                      @click="toggleInfo(benchmark.methodology)"
                    >
                      <InfoIcon />
                      <Translation
                        name="benchmarks_show.about_methodology"
                        default="About Methodology"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div
    class="fixed right-0 top-14 lg:top-0 h-full w-full sm:w-[360px] bg-white flex flex-col z-50 p-6 transform transition-transform duration-300 ease-in-out"
    :class="isInfoOpen ? 'translate-x-0' : 'translate-x-full'"
  >
    <div class="flex justify-between items-start mb-4">
      <div
        class="bg-brand-25 rounded-full h-12 w-12 md:h-16 md:w-16 flex items-center justify-center"
      >
        <InfoIcon class="h-6 w-6 md:h-8 md:w-8" />
      </div>
      <button @click="closeInfo()">
        <CloseIcon class="h-6 w-6 md:h-8 md:w-8" />
      </button>
    </div>
    <h2 class="text-gray-900 text-lg sm:text-xl md:text-2xl font-semibold mb-4">
      {{ methodologyName(infoMethodology) }}
    </h2>

    <p v-if="infoMethodology === 'anker'" class="text-gray-900 text-sm">
      <Translation
        name="benchmarks_show.anker_info"
        default="If you choose an Anker report, please select the report corresponding to your wage data year. If a full fledged methodology report is available, please select this report. The value of the respective benchmark will be available as soon as you select the report. In case you wish to view the benchmark report, you will be able to access this publicly via below link by typing your region in the search bar."
      />
    </p>
    <p
      v-if="infoMethodology === 'wage-indicator'"
      class="text-gray-900 text-sm"
    >
      <Translation
        name="benchmarks_show.wage_indicator_info"
        default="If you choose a WageIndicator report"
      />
    </p>
    <p v-if="infoMethodology === 'fair-wage'" class="text-gray-900 text-sm">
      <Translation
        name="benchmarks_show.fair_wage_info"
        default="If you choose a Fair Wage report"
      />
    </p>
    <p
      v-if="infoMethodology === 'living-wage-us'"
      class="text-gray-900 text-sm"
    >
      <Translation
        name="benchmarks_show.living_wage_us_info"
        default="If you choose a Living Wage US report"
      />
    </p>
    <p v-if="infoMethodology === 'other'" class="text-gray-900 text-sm">
      <Translation
        name="benchmarks_show.other_info"
        default="If you choose an Other report"
      />
    </p>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, reactive } from "vue";
import ChevronDown from "../icons/ChevronDown.vue";
import ExternalLinkIcon from "../icons/ExternalLinkIcon.vue";
import InfoIcon from "../icons/InfoIcon.vue";
import CloseIcon from "../icons/CloseIcon.vue";
import SearchableDropdown from "../SearchableDropdown.vue";

const props = defineProps({
  benchmarks: {
    type: Array,
    required: true,
  },
  countryName: {
    type: String,
    required: true,
  },
  allRegionsTranslation: {
    type: String,
    required: true,
  },
  allYearsTranslation: {
    type: String,
    required: true,
  },
  allMethodologiesTranslation: {
    type: String,
    required: true,
  },
});

const isInfoOpen = ref(false);
const infoMethodology = ref("");

const toggleInfo = (methodology) => {
  isInfoOpen.value = !isInfoOpen.value;
  infoMethodology.value = methodology;
};

const closeInfo = () => {
  isInfoOpen.value = false;
};

const methodologyName = (methodology) => {
  switch (methodology) {
    case "anker":
      return "Full-Fledged Anker Methodology";
    case "living-wage-us":
      return "Living Wage US Methodology";
    case "wage-indicator":
      return "WageIndicator Typical Family Methodology";
    case "fair-wage":
      return "Fair Wage Network Typical Family Methodology";
    case "other":
      return "Other";
    default:
      return "Other";
  }
};

const getDisplayRegionName = (benchmark) => {
  return benchmark.country_region || benchmark.iso_region_name;
};

const methodologyColor = (methodology) => {
  switch (methodology) {
    case "anker":
      return "bg-idh-blue";
    case "living-wage-us":
      return "bg-living-wage-purple";
    case "wage-indicator":
      return "bg-yellow-primary";
    case "fair-wage":
      return "bg-light-green";
    case "other":
      return "bg-brand-600";
    default:
      return "bg-gray-400";
  }
};

const regionCount = computed(() => {
  const unique = new Set(props.benchmarks.map((b) => getDisplayRegionName(b)));
  return unique.size;
});

const isBenchmarkOpen = reactive({});

props.benchmarks.forEach((b) => {
  isBenchmarkOpen[b.id] = false;
});

const toggleBenchmark = (id) => {
  isBenchmarkOpen[id] = !isBenchmarkOpen[id];
};

const selectedRegion = ref(props.allRegionsTranslation);
const yearDropdown = ref(null);
const selectedYear = ref(props.allYearsTranslation);
const isYearDropdownOpen = ref(false);
const methodologyDropdown = ref(null);
const selectedMethodology = ref({
  value: null,
  label: props.allMethodologiesTranslation,
});
const isMethodologyDropdownOpen = ref(false);

const handleClickOutside = (targetRef, callback) => {
  const handleClick = (e) => {
    if (targetRef.value && !targetRef.value.contains(e.target)) {
      callback();
    }
  };
  onMounted(() => document.addEventListener("click", handleClick));
  onUnmounted(() => document.removeEventListener("click", handleClick));
};

handleClickOutside(yearDropdown, () => (isYearDropdownOpen.value = false));
handleClickOutside(
  methodologyDropdown,
  () => (isMethodologyDropdownOpen.value = false)
);

const regions = computed(() => {
  const set = new Set(
    props.benchmarks
      .filter((b) => b.iso_region !== "COUNTRY_WIDE")
      .map((b) => b.iso_region_name)
      .filter(Boolean)
  );
  return [props.allRegionsTranslation, ...Array.from(set)];
});

const years = computed(() => {
  const set = new Set(props.benchmarks.map((b) => b.year).filter(Boolean));
  return [props.allYearsTranslation, ...Array.from(set).sort((a, b) => b - a)];
});

const methodologies = computed(() => {
  const set = new Set(
    props.benchmarks.map((b) => b.methodology).filter(Boolean)
  );
  return [
    { value: null, label: props.allMethodologiesTranslation },
    ...Array.from(set).map((m) => ({
      value: m,
      label: m
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" "),
    })),
  ];
});

const activeFilterPills = computed(() => {
  const pills = [];
  if (selectedRegion.value !== props.allRegionsTranslation) {
    pills.push({
      key: "region",
      label: selectedRegion.value,
      remove: () => {
        selectedRegion.value = props.allRegionsTranslation;
      },
    });
  }
  if (selectedYear.value !== props.allYearsTranslation) {
    pills.push({
      key: "year",
      label: String(selectedYear.value),
      remove: () => {
        selectedYear.value = props.allYearsTranslation;
      },
    });
  }
  if (selectedMethodology.value.value !== null) {
    pills.push({
      key: "methodology",
      label: selectedMethodology.value.label,
      remove: () => {
        selectedMethodology.value = {
          value: null,
          label: props.allMethodologiesTranslation,
        };
      },
    });
  }
  return pills;
});

const filteredBenchmarks = computed(() => {
  let results;

  if (
    selectedRegion.value === props.allRegionsTranslation &&
    selectedYear.value === props.allYearsTranslation &&
    selectedMethodology.value.value === null
  ) {
    results = [...props.benchmarks];
  } else {
    results = props.benchmarks.filter((b) => {
      const isCountryWide = b.iso_region === "COUNTRY_WIDE";
      const regionMatch =
        selectedRegion.value === props.allRegionsTranslation ||
        b.iso_region_name === selectedRegion.value ||
        (selectedRegion.value !== props.allRegionsTranslation && isCountryWide);

      const yearMatch =
        selectedYear.value === props.allYearsTranslation ||
        b.year === selectedYear.value;

      const methodologyMatch =
        selectedMethodology.value.value === null ||
        b.methodology === selectedMethodology.value.value;

      return regionMatch && yearMatch && methodologyMatch;
    });
  }

  return results.sort((a, b) => {
    const yearDiff = (Number(b.year) || 0) - (Number(a.year) || 0);
    if (yearDiff !== 0) return yearDiff;

    const aIsCountryWide = a.iso_region === "COUNTRY_WIDE" ? 1 : 0;
    const bIsCountryWide = b.iso_region === "COUNTRY_WIDE" ? 1 : 0;
    return aIsCountryWide - bIsCountryWide;
  });
});
</script>
